import fb from '@/firebaseConfig';
import router from '@/router';
import analytics from '@/utils/Analytics';
import localStore from '@/utils/LocalStore';
import { getAuthParams } from '@/helpers/urlHelpers';

/* See Auth-View for notes on Auth flows & screens

  Firebase auth notes:
  - Can't add a password if already signed in with link
  - If had PW first & email verified, can sign in with either password or link
  - If had PW first & email NOT verified, signing in with link removes password method
  - Clicking on verify email link goes to blank page with Firebase UI 
    that says "Email verified. You can now sign in to your new account"
*/

/*

  STATE

 */

const state = {
  isSignedIn: false,
  screen: 'intro', // Current auth screen
  pendingEmail: '', // Email user is trying to sign in with
  emailLinkUrl: '',
  redirectAfterSignIn: '',
  signInMethod: 'emailLink', // or 'password'
  sendingEmail: false,
  signingInWithPassword: false,
  actionParams: {},
  error: null, // format: { code, message }
};

/*

  GETTERS

 */

const getters = {
  actionCodeSettings: (state, getters, rootState) => {
    // const { urlOrigin } = getters;
    const { isDevProject } = fb;
    const { isNativeApp } = rootState.device;

    if (isNativeApp) {
      return {
        // URL you want to redirect back to
        url: `https://${isDevProject ? 'dev.' : ''}app.imaginarie.app`,
        handleCodeInApp: true,
        iOS: {
          bundleId: isDevProject ? 'app.imaginarie.app.dev' : 'app.imaginarie.app',
        },
        android: {
          packageName: isDevProject ? 'app.imaginarie.app.dev' : 'app.imaginarie.app',
          installApp: true,
          minimumVersion: '23',
        },
        dynamicLinkDomain: isDevProject ? 'dev.link.imaginarie.app' : 'link.imaginarie.app',
      };
    }

    return { url: window.location.href, handleCodeInApp: true };
  },
};

/*

  ACTIONS

 */

const actions = {
  init({ commit, dispatch }) {
    fb.auth().onAuthStateChanged(user => {
      if (user) {
        commit('setSignedIn');
        dispatch('user/init', user, { root: true });
        analytics.setUserId(user.uid);
      } else {
        console.log('No user authenticated');
        commit('setSignedOut');
        commit('resetState');
        commit('user/clearUser', null, { root: true });
        analytics.setUserId();
      }
    });
  },

  // Determines which screen/step shows in auth flow
  // See Auth-View for details
  changeScreen({ commit }, screen) {
    commit('setScreen', screen);
    analytics.setCurrentScreen('Auth', screen);
  },

  // For all new or returning users
  // Dispatched after user enters email address on first auth screen
  // Decides whether to send email link or enter password flow
  async handleEnterEmail({ commit, dispatch }, { email, wantsPassword }) {
    if (!wantsPassword) {
      commit('setSendingEmail', true);
    }
    commit('setPendingEmail', email);
    commit('setError', null);

    const signInMethodsData = await fb.getSignInMethods(email);
    const { hasPassword, hasSignedInWithLink } = signInMethodsData;
    commit('setHasSignedInWithLink', hasSignedInWithLink);

    if (wantsPassword && !hasPassword && hasSignedInWithLink) {
      commit('setError', {
        code: 'auth/email-already-in-use',
        message: 'The email address is already in use by another account.',
      });
      analytics.exception('auth/email-already-in-use');
    } else if (wantsPassword && !hasPassword && !hasSignedInWithLink) {
      commit('setSignInMethod', 'password');
      dispatch('changeScreen', 'create-password');
    } else if ((wantsPassword && hasPassword) || (hasPassword && !hasSignedInWithLink)) {
      // Users with both password & email link will get sent link
      // unless they want to use password
      commit('setSignInMethod', 'password');
      commit('setSendingEmail', false);
      dispatch('changeScreen', 'enter-password');
    } else {
      commit('setSignInMethod', 'emailLink');
      dispatch('sendEmailLink', email);
    }
  },

  // Sends auth email link, same for new and returning
  async sendEmailLink({ commit, dispatch, getters }, email) {
    const { actionCodeSettings } = getters;

    try {
      const sendSignInLinkToEmail = fb.functions.httpsCallable('auth-sendSignInLinkToEmail');
      await sendSignInLinkToEmail({
        email,
        actionCodeSettings,
      });
      commit('setSendingEmail', false);
      dispatch('changeScreen', 'check-email');
      localStore.setItem('emailForSignIn', email);
    } catch (error) {
      commit('setSendingEmail', false);
      commit('setError', error);
      dispatch('changeScreen', 'error-sending-link');
      analytics.exception((error && error.code) || 'Error sending sign in link');
      console.error('Error sending sign in link', JSON.stringify(error));
    }
  },

  saveRedirect(context, redirect) {
    localStore.setItem('redirectAfterSignIn', redirect);
  },

  // If user can't find email, can resend either email link or password recovery
  resendEmail({ dispatch, state }) {
    const { pendingEmail, signInMethod } = state;
    if (signInMethod === 'password') {
      if (pendingEmail) {
        dispatch('sendPasswordResetLink', pendingEmail);
      } else {
        dispatch('changeScreen', 'forgot-password');
      }
    } else {
      dispatch('sendEmailLink', pendingEmail);
    }
  },

  // After user clicks the link in email,
  // app opens and determines that this url is a sign-in link,
  // and dispatches this function
  async handleSignInWithEmailLink({ dispatch }, url) {
    const email = await localStore.getItem('emailForSignIn');

    if (email) {
      dispatch('signInWithEmailLink', { email, url });
    } else {
      // User opened the link on a different device. To prevent session fixation
      // attacks, ask the user to provide the associated email again.
      dispatch('changeScreen', 'confirm-email');
    }
  },

  // User has re-entered email to finish signing in with url
  confirmEmailForLink({ dispatch, state }, email) {
    const url = state.emailLinkUrl;
    dispatch('signInWithEmailLink', { email, url });
  },

  // After confirming email and url, call FB signInWithEmailLink method
  async signInWithEmailLink({ commit, dispatch }, { email, url }) {
    dispatch('changeScreen', 'signing-in');
    commit('setSignInMethod', 'emailLink');

    fb.auth()
      .signInWithEmailLink(email, url)
      .then(result => {
        localStore.removeItem('emailForSignIn');
        dispatch('handleSignInSuccess', result);
      })
      .catch(error => {
        const { code } = error;

        if (code === 'auth/invalid-action-code') {
          // Clicking on old link - code: "auth/invalid-action-code"
          // message: "The action code is invalid. This can happen if the code is malformed, expired, or has already been used."
          dispatch('changeScreen', 'error-expired-link');
        } else if (code === 'auth/invalid-email') {
          // Confirm wrong email address - code: "auth/invalid-email"
          // message: "The email provided does not match the sign-in email address."
          dispatch('changeScreen', 'error-wrong-email');
        } else {
          commit('setError', error);
          dispatch('changeScreen', 'error-other');
        }
        analytics.exception(code || 'Error signing in with email link');
        console.error('Error signing in with email link', JSON.stringify(error));
      });
  },

  createUserWithPassword({ commit, dispatch }, { email, password }) {
    commit('setError', null);
    commit('setSignInMethod', 'password');
    commit('setSigningInWithPassword', true);
    commit('setPendingEmail', email);

    fb.auth()
      .createUserWithEmailAndPassword(email, password)
      .then(result => {
        commit('setSigningInWithPassword', false);
        dispatch('sendEmailVerification');
        dispatch('handleSignInSuccess', result);
      })
      .catch(error => {
        commit('setSigningInWithPassword', false);
        console.error('Error creating account with password', JSON.stringify(error));
      });
  },

  signInWithPassword({ commit, dispatch }, { email = '', password = '' }) {
    commit('setError', null);
    commit('setSignInMethod', 'password');
    commit('setSigningInWithPassword', true);
    commit('setPendingEmail', email);

    fb.auth()
      .signInWithEmailAndPassword(email, password)
      .then(result => {
        commit('setSigningInWithPassword', false);
        dispatch('handleSignInSuccess', result);
      })
      .catch(error => {
        commit('setError', error);
        commit('setSigningInWithPassword', false);
        analytics.exception((error && error.code) || 'Error signing in with password');
        console.error('Error signing in with password', JSON.stringify(error));
      });
  },

  async handleSignInSuccess({ commit, state }, authResult) {
    commit('setError', null);

    const { uid } = authResult.user;
    console.log('handleSignInSuccess', uid);
    const { isNewUser } = authResult.additionalUserInfo;
    const { signInMethod } = state;

    analytics.userSignedIn(uid, isNewUser, signInMethod);

    const redirect = await localStore.getItem('redirectAfterSignIn');
    localStore.removeItem('redirectAfterSignIn');

    if (redirect) {
      router.replace(redirect);
    } else {
      router.replace('/');
    }
  },

  async sendPasswordResetLink({ commit, dispatch, getters }, email = '') {
    commit('setSignInMethod', 'password');
    commit('setSendingEmail', true);
    commit('setPendingEmail', email);
    commit('setError', null);

    const { actionCodeSettings } = getters;

    try {
      const sendPasswordResetEmail = fb.functions.httpsCallable('auth-sendPasswordResetEmail');
      await sendPasswordResetEmail({
        email,
        actionCodeSettings,
      });
      commit('setSendingEmail', false);
      dispatch('changeScreen', 'check-email');
    } catch (error) {
      commit('setSendingEmail', false);
      commit('setError', error);
      dispatch('changeScreen', 'error-sending-link');
      analytics.exception((error && error.code) || 'Error sending password recovery link');
      console.error('Error sending password recovery link', JSON.stringify(error));
    }
  },

  // Any PW user can switch to email link,
  // but accounts with unverified email won't be able to use PW again
  switchToEmailLink({ commit, dispatch }, email) {
    commit('setSendingEmail', true);
    commit('setPendingEmail', email);
    commit('setError', null);

    commit('setSignInMethod', 'emailLink');
    dispatch('sendEmailLink', email);
  },

  // Sent to password users after they sign in
  async sendEmailVerification({ dispatch, getters }, showSnack) {
    const { email } = fb.auth().currentUser || {};
    const { actionCodeSettings } = getters;

    try {
      const sendEmailVerification = fb.functions.httpsCallable('auth-sendEmailVerification');
      await sendEmailVerification({
        email,
        actionCodeSettings,
      });
      if (showSnack) {
        dispatch(
          'ui/updateSnack',
          { text: 'Verification email sent', quick: true },
          { root: true }
        );
      }
    } catch (error) {
      if (showSnack) {
        dispatch(
          'ui/updateSnack',
          { text: 'Error sending verification email. Try again later' },
          { root: true }
        );
      }
      analytics.exception((error && error.code) || 'Error sending email verification');
      console.error('Error sending email verification', error);
    }
  },

  // IMPORTANT: Auth actions like reset password and verify email use the Action URL
  // which is the LIVE page at [dev.]app.imaginarie.app
  // There's no localhost equivalent (asfaik)
  handleAuthActionLink({ state, commit, dispatch }, url) {
    dispatch('changeScreen', 'loading');
    const params = getAuthParams(url);
    commit('setActionParams', params);
    const { mode } = params;

    if (state.isSignedIn) router.replace({ name: 'AuthUpdate' });
    if (mode === 'resetPassword') {
      dispatch('handleResetPasswordLink');
    } else if (mode === 'verifyEmail') {
      dispatch('handleVerifyEmailLink');
    } else {
      console.error('Invalide mode for auth action link');
    }
    // mode === 'recoverEmail'
    // Not using unless we give options to change email address
    // This would allow a user to cancel email address change
  },

  handleResetPasswordLink({ commit, dispatch, state }) {
    commit('setSignInMethod', 'password');
    commit('setError', null);

    const { oobCode: actionCode } = state.actionParams;

    fb.auth()
      .verifyPasswordResetCode(actionCode)
      .then(email => {
        commit('setPendingEmail', email);
        dispatch('changeScreen', 'reset-password');
      })
      .catch(error => {
        commit('setError', error);
        dispatch('changeScreen', 'error-expired-link');
        analytics.exception((error && error.code) || 'Error with reset password link');
      });
  },

  saveNewPassword({ commit, dispatch, state }, newPassword) {
    const { oobCode: actionCode } = state.actionParams;
    const { pendingEmail } = state;

    commit('setSigningInWithPassword', true);

    fb.auth()
      .confirmPasswordReset(actionCode, newPassword)
      .then(() => {
        dispatch('signInWithPassword', { email: pendingEmail, password: newPassword });
      })
      .catch(error => {
        commit('setError', error);
        analytics.exception((error && error.code) || 'Error resetting password');
        console.error('Error resetting password', error);
      });
  },

  handleVerifyEmailLink({ commit, dispatch }) {
    const { oobCode: actionCode } = state.actionParams;

    fb.auth()
      .applyActionCode(actionCode)
      .then(() => {
        dispatch('changeScreen', 'email-verified');
      })
      .catch(error => {
        // Code is invalid or expired
        commit('setError', error);
        dispatch('changeScreen', 'error-verification-link');
        analytics.exception((error && error.code) || 'Error with verification email');
      });
  },

  signOut({ dispatch }) {
    fb.auth().signOut();
    dispatch('purchases/reset', null, { root: true });
    dispatch('cards/clearRecentIds', null, { root: true });
    dispatch('stacks/clearRecentIds', null, { root: true });
    analytics.userSignedOut();
    window.location.reload();
    // dispatch('cards/unsubscribeToCardSnapshots', null, { root: true });
    console.log('Signed out');
  },
};

/*

  MUTATIONS

 */

const mutations = {
  setSignedIn(state) {
    state.isSignedIn = true;
    state.error = null;
  },

  setScreen(state, screen) {
    state.screen = screen;
  },

  setPendingEmail(state, email) {
    state.pendingEmail = email;
  },

  setSignInMethod(state, method) {
    state.signInMethod = method;
  },
  setHasSignedInWithLink(state, hasSignedInWithLink) {
    state.hasSignedInWithLink = hasSignedInWithLink;
  },

  setSendingEmail(state, sendingEmail) {
    state.sendingEmail = sendingEmail;
  },

  setSigningInWithPassword(state, signingInWithPassword) {
    state.signingInWithPassword = signingInWithPassword;
  },

  setEmailLinkUrl(state, url) {
    state.emailLinkUrl = url;
  },

  setActionParams(state, params) {
    state.actionParams = params;
  },

  setError(state, error = null) {
    state.error = error;
  },

  // Called when user signs out, and from Auth-View destroyed method
  resetState(state) {
    state.screen = 'intro';
    state.pendingEmail = '';
    state.emailLinkUrl = '';
    state.redirectAfterSignIn = '';
    state.signInMethod = 'emailLink';
    state.sendingEmail = false;
    state.signingInWithPassword = false;
    state.actionParams = {};
    state.error = null;
  },

  setSignedOut(state) {
    state.isSignedIn = false;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
